/**
 * Règles de validation centralisées — username / phone / email / password
 * Modifier ici pour que le changement s'applique à tous les formulaires
 * (Admin, Enseignant, Parent)
 */

import { isValidPhoneLength } from '../constants/countries';

// ─── Constantes ───────────────────────────────────────────────────────────────

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MIN_PASSWORD_LENGTH = 6;
export const MIN_USERNAME_LENGTH = 3;

// ─── Username ─────────────────────────────────────────────────────────────────

/**
 * Valide un nom d'utilisateur.
 * @param username Nom d'utilisateur saisi
 * @param required Vrai par défaut
 * @returns Message d'erreur en arabe, ou null si valide
 */
export function validateUsername(username: string, required = true): string | null {
  if (!username || username.trim() === '') {
    return required ? 'اسم المستخدم مطلوب' : null;
  }
  if (username.trim().length < MIN_USERNAME_LENGTH) {
    return `اسم المستخدم يجب أن يكون ${MIN_USERNAME_LENGTH} أحرف على الأقل`;
  }
  return null;
}

// ─── Phone ────────────────────────────────────────────────────────────────────

/**
 * Valide un numéro de téléphone.
 * @param phone   Numéro brut (local, avant formatage international)
 * @param required Vrai par défaut ; false pour les champs optionnels
 * @returns Message d'erreur en arabe, ou null si valide
 */
export function validatePhone(phone: string, required = true): string | null {
  if (!phone || phone.trim() === '') {
    return required ? 'رقم الهاتف مطلوب' : null;
  }
  if (!isValidPhoneLength(phone)) {
    return 'تنسيق رقم الهاتف غير صحيح (8 أرقام على الأقل)';
  }
  return null;
}

// ─── Email ────────────────────────────────────────────────────────────────────

/**
 * Valide une adresse e-mail.
 * @param email    Adresse saisie
 * @param required Vrai par défaut ; false pour les champs optionnels
 * @returns Message d'erreur en arabe, ou null si valide
 */
export function validateEmail(email: string, required = true): string | null {
  if (!email || email.trim() === '') {
    return required ? 'البريد الإلكتروني مطلوب' : null;
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return 'تنسيق البريد الإلكتروني غير صحيح';
  }
  return null;
}

// ─── Password ─────────────────────────────────────────────────────────────────

/**
 * Valide un mot de passe.
 * @param password        Mot de passe saisi
 * @param confirmPassword Confirmation (optionnelle — vérifie la correspondance si fournie)
 * @param required        Vrai par défaut ; mettre false en mode édition (champ optionnel)
 * @returns Message d'erreur en arabe, ou null si valide
 */
export function validatePassword(
  password: string,
  confirmPassword?: string,
  required = true,
): string | null {
  if (!password || password.trim() === '') {
    return required ? 'كلمة المرور مطلوبة' : null;
  }
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `كلمة المرور يجب أن تكون ${MIN_PASSWORD_LENGTH} أحرف على الأقل`;
  }
  if (confirmPassword !== undefined && password !== confirmPassword) {
    return 'كلمة المرور وتأكيدها غير متطابقتين';
  }
  return null;
}
